import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';
import type { SupplyPosition } from '../sp-types/positions';

export type { SupplyPosition } from '../sp-types/positions';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(null);
    }, timeoutMs);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// SupplyPosition type is defined in sp-types/positions.ts and re-exported above

export interface GetSupplyPositionsParams {
  accountId?: number;
  formulaId?: number;
  startDate?: string;
  endDate?: string;
  supplyType?: string;
  location?: string;
}

function getPreviousYearStart(): string {
  const now = new Date();
  const previousYear = now.getFullYear() - 1;
  return `${previousYear}-01-01`;
}

export async function getSupplyPositions(params: GetSupplyPositionsParams = {}): Promise<SupplyPosition[]> {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams();
  
  // Default to previous year's Jan 1 if no startDate provided
  const startDate = params.startDate || getPreviousYearStart();
  
  if (params.accountId) queryParams.append('accountId', params.accountId.toString());
  if (params.formulaId) queryParams.append('formulaId', params.formulaId.toString());
  queryParams.append('startDate', startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.supplyType) queryParams.append('supplyType', params.supplyType);
  if (params.location) queryParams.append('location', params.location);

  const url = `${API_URL}/api/v2/supply/positions?${queryParams.toString()}`;
  console.log('[supplyService] Fetching positions from:', url);

  const response = await fetch(url, { headers });

  if (!response.ok) {
    console.error('[supplyService] API error:', response.status, response.statusText);
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch supply positions');
  }

  const data = await response.json();
  console.log('[supplyService] Positions response:', data);
  return (data.positions || []).map((p: any): SupplyPosition => ({ ...p, category: 'supply' as const }));
}
